/**
 * SEO Pages · CRM index page (/admin/seo-pages).
 *
 * A live index of every page in the BrightLabs SEO cluster. Not a CMS —
 * these pages are hand-crafted React components. This screen exists so the
 * team can grab links, verify status and see primary search intent at a
 * glance without hunting through the codebase.
 *
 * When a new SEO page ships, append it to SEO_PAGES below.
 */
import { useMemo, useState } from 'react';
import { Copy, ExternalLink, CheckCircle2, Clock, Search } from 'lucide-react';
import { toast } from 'sonner';

const SEO_PAGES = [
  {
    n: '01',
    path: '/norwich/digital-agency',
    title: 'Digital Agency Norwich',
    intent: 'digital agency Norwich',
    tranche: 1,
    status: 'live',
    concept: 'Four-discipline scroll interactive (Build · Create · Grow · Automate)',
    cluster: 'Norwich',
  },
  {
    n: '02',
    path: '/norwich/web-design',
    title: 'Web Design Norwich',
    intent: 'web design Norwich',
    tranche: 1,
    status: 'live',
    concept: 'Interactive 8-point Website Health Check with browser mockup + floating marker',
    cluster: 'Norwich',
  },
  {
    n: '03',
    path: '/norwich/seo',
    title: 'SEO Agency Norwich',
    intent: 'SEO agency Norwich',
    tranche: 2,
    status: 'live',
    concept: 'Search Footprint constellation that grows through 6 stages',
    cluster: 'Norwich',
  },
  {
    n: '04',
    path: '/norwich/google-ads',
    title: 'Google Ads Agency Norwich',
    intent: 'Google Ads agency Norwich',
    tranche: 2,
    status: 'live',
    concept: 'Commercial funnel with visible leak indicators at each stage',
    cluster: 'Norwich',
  },
  {
    n: '05',
    path: '/norwich/social-media',
    title: 'Social Media Agency Norwich',
    intent: 'social media agency Norwich',
    tranche: 3,
    status: 'live',
    concept: 'Moving creative-studio strip + 7-step content pipeline',
    cluster: 'Norwich',
  },
  {
    n: '06',
    path: '/norwich/software-development',
    title: 'Software Development Norwich',
    intent: 'software development Norwich',
    tranche: 3,
    status: 'live',
    concept: 'Animated systems map — central hub, satellite nodes, connections light up',
    cluster: 'Norwich',
  },
  {
    n: '07',
    path: '/insights/website-redesign-vs-rebuild',
    title: 'Website Redesign vs Rebuild?',
    intent: 'website redesign vs rebuild',
    tranche: 4,
    status: 'live',
    concept: 'Interactive decision tool — 7 scored questions → Keep · Improve · Redesign · Rebuild',
    cluster: 'Insights',
  },
  {
    n: '08',
    path: '/services/custom-crm-development',
    title: 'Custom CRM Development',
    intent: 'custom CRM development',
    tranche: 4,
    status: 'live',
    concept: 'Interactive workflow builder — 6-stage pipeline + 7 attachable modules that light up connected stages',
    cluster: 'Services',
  },
  {
    n: '09',
    path: '/insights/business-website-cost-uk',
    title: 'How Much Does a Business Website Cost?',
    intent: 'how much does a website cost UK',
    tranche: 5,
    status: 'live',
    concept: 'Interactive Website Scope Builder — 10 scope toggles → Simple / Moderate / Complex bands with UK price ranges',
    cluster: 'Insights',
  },
  {
    n: '10',
    path: '/insights/seo-vs-google-ads',
    title: 'SEO vs Google Ads',
    intent: 'SEO vs Google Ads',
    tranche: 5,
    status: 'live',
    concept: 'Split-screen lean meter — 7 factor inputs tilt between SEO · Paid · Both · Fix Something Else First',
    cluster: 'Insights',
  },
  {
    n: '14',
    path: '/industries/saas-b2b-tech',
    title: 'Digital Marketing for SaaS & B2B Tech Companies',
    intent: 'digital marketing for SaaS companies',
    tranche: 6,
    status: 'live',
    concept: 'The SaaS Growth Stack — 8 vertical stages (Product → Retention) with click-through detail panel · where growth breaks + which service holds it together',
    cluster: 'Industries',
  },
  { n: '11', path: '/london/digital-agency', title: 'Digital Agency London', intent: 'digital agency London', tranche: 6, status: 'live', concept: 'Two operating-model comparison — 6-layer traditional agency stack vs BrightLabs direct-to-doer model, with hover reveals', cluster: 'Locations' },
  { n: '12', path: '/b2b-digital-marketing-agency', title: 'B2B Digital Marketing Agency UK', intent: 'B2B digital marketing agency UK', tranche: 6, status: 'live', concept: 'The B2B growth funnel — 7 clickable stages Attention → Revenue with disciplines mapped to each', cluster: 'Insights' },
  { n: '13', path: '/industries/recruitment-agencies', title: 'Digital Marketing for Recruitment Agencies', intent: 'digital marketing for recruitment agencies', tranche: 6, status: 'live', concept: 'Two parallel acquisition journeys — Client vs Candidate — toggled side-by-side', cluster: 'Industries' },
  { n: '15', path: '/industries/private-clinics', title: 'Digital Marketing for Private Clinics', intent: 'digital marketing for private clinics', tranche: 6, status: 'live', concept: 'The 7-stage patient acquisition journey — click any stage to see leaks + fixes', cluster: 'Industries' },
  { n: '16', path: '/insights/website-not-generating-leads', title: 'Why isn\u2019t my website generating leads?', intent: 'website not generating leads', tranche: 6, status: 'live', concept: 'Five-question Yes/No diagnostic → 5 verdicts (Visibility / Targeting / Positioning / Conversion / Process)', cluster: 'Insights' },
  { n: '17', path: '/insights/google-ads-clicks-no-leads', title: 'Google Ads clicks but no leads', intent: 'Google Ads no conversions', tranche: 6, status: 'live', concept: 'PPC funnel leak diagnostic — 7 stages, first is fine, six risk points with click-through detail', cluster: 'Insights' },
  { n: '18', path: '/insights/when-do-you-need-custom-crm', title: 'When does a business need a custom CRM?', intent: 'when do I need a custom CRM', tranche: 6, status: 'live', concept: 'CRM tipping-point tool — tick 9 complexity signals to score into Off-the-shelf / Configure / Integrate / Custom bands', cluster: 'Insights' },
  { n: '19', path: '/insights/in-house-marketing-vs-agency', title: 'In-house marketing team vs agency', intent: 'in-house marketing vs agency', tranche: 6, status: 'live', concept: '8-factor decision matrix leaning between In-house / Agency / Hybrid verdicts', cluster: 'Insights' },
  { n: '20', path: '/insights/marketing-agency-vs-growth-partner', title: 'Marketing agency vs Growth Partner', intent: 'marketing agency vs growth partner', tranche: 6, status: 'live', concept: 'Two operating models compared side-by-side — Model A (5 suppliers → client) vs Model B (one strategy → connected disciplines)', cluster: 'Insights' },
];

const CLUSTERS = ['All', 'Norwich', 'Locations', 'Insights', 'Services', 'Industries'];

const PROD_ORIGIN = 'https://brightlabs.live';

export default function AdminSeoPages() {
  const [cluster, setCluster] = useState('All');
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    let list = SEO_PAGES;
    if (cluster !== 'All') list = list.filter((p) => p.cluster === cluster);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(needle) ||
          p.intent.toLowerCase().includes(needle) ||
          p.concept.toLowerCase().includes(needle) ||
          p.path.toLowerCase().includes(needle)
      );
    }
    return list;
  }, [cluster, q]);

  const liveCount = SEO_PAGES.filter((p) => p.status === 'live').length;
  const previewOrigin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  function copy(text, label) {
    try {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (e) {
      toast.error('Copy failed — long-press to copy manually');
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6 md:p-10" data-testid="admin-seo-pages">
      <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 font-mono">Client Advisory</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-2 text-white">SEO Pages</h1>
          <p className="text-slate-400 mt-2 max-w-[62ch]">
            The BrightLabs organic-search cluster. Live and planned pages, primary search intent, and
            quick links. Grab a link, share it with a client, or drop it into a proposal.
          </p>
        </div>
        <div className="flex items-baseline gap-6">
          <Stat label="Live" value={liveCount} />
          <Stat label="Total" value={SEO_PAGES.length} />
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2">
        {CLUSTERS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setCluster(c)}
            data-testid={`admin-seo-cluster-${c.toLowerCase()}`}
            className={`text-[11px] uppercase tracking-[0.22em] font-mono px-3.5 py-2 rounded-full transition ${
              cluster === c
                ? 'bg-white text-slate-900 shadow-lg shadow-white/10'
                : 'bg-slate-900 text-slate-300 ring-1 ring-slate-700 hover:ring-slate-500 hover:text-white'
            }`}
          >
            {c}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search intent, page, concept…"
            data-testid="admin-seo-search"
            className="pl-8 pr-4 py-2 text-sm bg-slate-900 text-slate-100 placeholder:text-slate-500 ring-1 ring-slate-700 focus:ring-slate-500 outline-none rounded-full w-[280px] transition"
          />
        </div>
      </div>

      <div className="mt-8 space-y-3">
        {filtered.length === 0 && (
          <div className="text-sm text-slate-500 py-10 text-center border border-dashed border-slate-700 rounded-lg">
            No pages match that filter.
          </div>
        )}
        {filtered.map((p) => {
          const previewUrl = `${previewOrigin}${p.path}`;
          const prodUrl = `${PROD_ORIGIN}${p.path}`;
          return (
            <article
              key={p.path}
              className="grid grid-cols-1 md:grid-cols-[60px_1.4fr_1fr_auto] gap-4 md:gap-8 items-start bg-slate-800/50 ring-1 ring-slate-700 rounded-lg p-5 md:p-6 hover:ring-slate-500 transition"
              data-testid={`admin-seo-row-${p.n}`}
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-slate-500 pt-1">{p.n}</div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base md:text-lg font-semibold text-white">{p.title}</span>
                  <StatusPill status={p.status} tranche={p.tranche} />
                </div>
                <div className="mt-1 text-[13px] text-slate-400 font-mono">{p.path}</div>
                <div className="mt-3 text-[13px] text-slate-300 leading-relaxed">{p.concept}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono">Primary intent</div>
                <div className="mt-1 text-[14px] text-slate-200">{p.intent}</div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-mono">Cluster</div>
                <div className="mt-1 text-[14px] text-slate-300">{p.cluster}</div>
              </div>
              <div className="flex flex-col gap-2 md:items-end min-w-[180px]">
                {p.status === 'live' && previewOrigin && (
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-seo-preview-${p.n}`}
                    className="text-[11px] uppercase tracking-[0.22em] font-mono inline-flex items-center gap-1.5 text-white hover:text-orange-400 transition"
                  >
                    Open preview <ExternalLink size={11} />
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => copy(prodUrl, 'Production URL')}
                  data-testid={`admin-seo-copy-prod-${p.n}`}
                  className="text-[11px] uppercase tracking-[0.22em] font-mono inline-flex items-center gap-1.5 text-slate-300 hover:text-white transition"
                >
                  Copy production URL <Copy size={11} />
                </button>
                {p.status === 'live' && previewOrigin && (
                  <button
                    type="button"
                    onClick={() => copy(previewUrl, 'Preview URL')}
                    data-testid={`admin-seo-copy-preview-${p.n}`}
                    className="text-[11px] uppercase tracking-[0.22em] font-mono inline-flex items-center gap-1.5 text-slate-400 hover:text-white transition"
                  >
                    Copy preview URL <Copy size={11} />
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      <div className="mt-10 text-[12px] text-slate-500 leading-relaxed max-w-[70ch]">
        <strong className="text-slate-300">Note:</strong> production URLs (<code className="text-slate-400">brightlabs.live/…</code>)
        will only work after the next production deploy. Preview URLs work now for review + sharing with
        the team internally. Once all 20 pages are approved, we&rsquo;ll add contextual links from the
        main services pages, the Norwich hub and the footer.
      </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-mono">{label}</div>
      <div className="text-3xl font-bold tracking-tight mt-1 text-white">{value}</div>
    </div>
  );
}

function StatusPill({ status, tranche }) {
  if (status === 'live') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-mono bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/40 rounded-full px-2 py-0.5">
        <CheckCircle2 size={10} /> Live · T{tranche}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-mono bg-slate-800 text-slate-400 ring-1 ring-slate-700 rounded-full px-2 py-0.5">
      <Clock size={10} /> Planned · T{tranche}
    </span>
  );
}
